import React from 'react';
import { classNames } from '@progress/kendo-react-common';
import { MaskedTextBox as KendoInput } from '@progress/kendo-react-inputs';
import * as _ from 'lodash';
import uuid from 'uuid-random';
import { PureComponent } from '../../../../dispatcher/modules';
import PuzzleButton from '../../button/PuzzleButton';
import {
  EVENTS_ACTION,
  PUZZLE_INPUT_ACTION_CONFIG,
  PUZZLE_INPUT_PROPS
} from './Types';
import { PUZZLE_FIELD_STATE, ACTION_BTN_SETTINGS } from '../Types';
import Field from '../../../layout/field/Field';
import WithTooltip from '../../../../utility/withTooltip';
import PuzzleTooltip from '../../tooltip/PuzzleTooltip';
import {
  ABSTRACT_FIELD_OMITTED_PROPS,
  FIELD_OMITTED_PROPS,
  TOOLTIP_OMITTED_PROPS
} from '../../../../utility';
import { Label } from '@progress/kendo-react-labels';

/**
 * This component is an extended version of Kendo's Input. It supports
 * state management mechanism flagging every state in which component can exist(empty/disabled/invalid),
 * presenting component with search icon on the left side of input or with action button on the right which you can fully customize
 **/
class PuzzleInput extends PureComponent<PUZZLE_INPUT_PROPS, PUZZLE_FIELD_STATE> {
  moduleId = uuid();
  inputReference: any;
  tooltip: any = null;
  inputID: any = null;

  static defaultProps = {
    tooltipAnchor: 'target',
    tooltipOpenDelay: 1000,
    size: 'medium'
  };

  constructor(props) {
    super(props);
    this.inputID = props.id || this.inputReference?._input?.id;
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.additionalEventsAction(EVENTS_ACTION.remove);
  }

  /**
   * Action button click handler
   * @param buttonType buttonType from ACTION_BTN_SETTINGS
   * @param e clickEvent
   */
  private actionBtnOnClickHandler = (buttonType: string, e: React.MouseEvent<HTMLButtonElement>) => {
    if (buttonType === 'delete') {
      this.inputReference?.focus();
    }

    const itemByType: ACTION_BTN_SETTINGS | undefined = _.find(this.props.actionBtnSettings, { 
      buttonType
    });
    itemByType?.onClick && itemByType.onClick(e);
  };

  /**
   * Action button render /
   */
  private actionBtnRender = () => {
    const {
      value,
      actionBtnSettings
    } = this.props;

    return actionBtnSettings?.map((button: PUZZLE_INPUT_ACTION_CONFIG, index: number) => {
      const buttonActualProps = _.omit(
        button,
        ['buttonType', 'ariaLabel']
      );

      return button.buttonType === 'delete' ? value ? <PuzzleButton
        {...buttonActualProps}
        key={index}
        aria-label={button.ariaLabel}
        onClick={this.actionBtnOnClickHandler.bind(this, button.buttonType)}
        fillMode="flat"
        iconClass={'tp-icon tp-x'}
      /> : null : <PuzzleButton
        {...buttonActualProps}
        key={index}
        aria-label={button.ariaLabel}
        onClick={this.actionBtnOnClickHandler.bind(this, button.buttonType)}
      />;
    });
  };

  /**
   * Adds or remove events that Kendo doesn't support
   */
  private additionalEventsAction = (action: EVENTS_ACTION) => {
    const eventsMap = [{
      name: 'keydown',
      fn: 'onKeyDown'
    }, {
      name: 'keyup',
      fn: 'onKeyUp'
    }, {
      name: 'paste',
      fn: 'onPaste'
    }, {
      name: 'drop',
      fn: 'onDrop'
    }];
    eventsMap.forEach((item) => {
      if (this.inputReference?.element && this.inputReference?.element[action] && _.isFunction(this.props[item.fn])) {
        this.inputReference.element[action](item.name, (e) => {
          this.triggerEvent(item.fn, e, true);
        });
      }
    });
  }

  /**
   * Sets field valid state
   * @param resourceExists
   */
  setValid = (resourceExists: boolean) => {
    _.get(this, 'props.setValid') && this.props.setValid(resourceExists);
  };

  renderBody() {
    const {
      asField,
      actionBtnSettings = [],
      ariaDescribedBy,
      className = '',
      dataCy = 'puzzle-input-wrap',
      disabled,
      inputClassName = '',
      label,
      labelClassName = '',
      locked = false,
      errorTxt,
      value,
      valid,
      searchField,
      size,
      withValidation,
      fieldClassName = '',
      fieldMandatory,
      fieldWithAdditionalEl,
      withTooltip,
      tooltipAnchor,
      tooltipContent,
      tooltipOpenDelay,
      tooltipPosition,
      tooltipClassName = '',
      titleText,
      customDataCy,
      password
    } = this.props;

    const asFieldProps = {
      fieldClassName,
      fieldMandatory,
      fieldWithAdditionalEl
    };

    const inputOmittedProps = [
      'actionBtnSettings', 'dataCy', 'inputClassName', 'searchField'
    ].concat(ABSTRACT_FIELD_OMITTED_PROPS, FIELD_OMITTED_PROPS, TOOLTIP_OMITTED_PROPS);

    const inputProps = _.omit(this.props, inputOmittedProps);

    const fieldCls = classNames(
      `tp-textbox-wrap ${className} k-flex-1 k-d-flex-col`,
      {
        'tp-with-action-btn': !_.isEmpty(actionBtnSettings),
        'tp-with-delete-action': _.find(this.props.actionBtnSettings, {
          buttonType: 'delete'
        }) !== undefined,
        'tp-with-multiple-actions': actionBtnSettings.length > 1,
        'tp-as-search-field': searchField,
        'k-state-empty': !value,
        'k-small': size === 'small',
        'k-large': size === 'large',
        'k-medium': size === 'medium',
        'k-state-invalid': valid === false,
        'k-state-disabled': disabled,
        'k-state-locked': locked
      }
    );

    const inputContent = (
      <KendoInput
        id={this.inputID}
        className={inputClassName}
        ref={(input: any) => {
          if (_.isUndefined(this.inputReference)) {
            this.inputReference = input;
            this.additionalEventsAction(EVENTS_ACTION.add);
          }
          if (password) {
            this.inputReference?._input?.setAttribute('type', 'password');
          }
        }}
        tabIndex={locked ? -1 : 0}
        {...inputProps}
        data-cy={dataCy && customDataCy ? dataCy : customDataCy ? undefined : 'puzzle-input'}
        title={titleText}
      />
    );

    const content = (
      <div
        className={fieldCls}
        data-cy={dataCy && customDataCy ? `${dataCy}-wrapper` : customDataCy ? undefined : dataCy}
      >
        <div className={'tp-textbox'}>
          {label && <Label
            editorId={this.inputID}
            className={classNames({
              [labelClassName]: labelClassName,
              'k-mandatory': !asField && fieldMandatory
            })}
          >
            {label}
          </Label>}
          {searchField &&
            <i
              aria-hidden={true}
              className="tp-icon tp-search k-hbox k-align-items-center"
              data-cy="search-icon"
            />
          }
          {withTooltip ?
            <WithTooltip
              className={tooltipClassName}
              context={this}
              title={titleText}
              htmlTag={'span'}
            >
              {inputContent}
            </WithTooltip> : inputContent
          }
          {this.actionBtnRender()}
        </div>
        {withValidation && <div
          data-cy={dataCy && customDataCy ? `${dataCy}-error-message` : customDataCy ? undefined : 'error-message'}
          className={'tp-validation-error-message'}
          id={ariaDescribedBy}
        >
          {errorTxt}
        </div>}
        <PuzzleTooltip
          anchorElement={tooltipAnchor}
          ref={(el: any) => this.tooltip = el}
          openDelay={tooltipOpenDelay}
          position={tooltipPosition}
          content={tooltipContent}
        />
      </div>
    );

    if (asField) {
      return (
        <Field {...asFieldProps}>
          {content}
        </Field>
      );
    }

    return content;
  }

  /**
   * Helper method for triggering events outside the cmp itself
   * */
  private triggerEvent(eventType: string, event: any, isAdditionalEvents?: boolean) {
    if (this.props[eventType]) {
      const argObj = isAdditionalEvents ? {
        event,
        target: this.inputReference
      } : {
        ...event,
        target: this.inputReference?._input
      };
      this.props[eventType].call(undefined, argObj);
    }
  }
}

export default PuzzleInput;
